import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { ActiveChallenge, ChallengeHistory, ChallengeStat, CreatableChallenge, Challenge, Invitation, PendingChallenge, SendInvite, SimpleLogin, ChallengeTemplate, Challenges, CurrentChallenge, ChallengeStatus } from './challenge.model';
import { ModuleManager } from 'src/shared/models/module-manager';

@Injectable({
	providedIn: 'root'
})
export class ChallengesService {

	private BASE_URL_CHALLENGES: string = '/api/v2/gamification/challenges';
	private BASE_URL_TEMPLATES: string = '/api/v2/gamification/challenge-templates';
	challenges: Challenges = null;
    availableCoins: number = 0;
    availableMetrics: CreatableChallenge[] = [];

	constructor(
		private cvApiService: CVApiService,
	) { }

	public async loadActiveChallenges(moduleKey: string, isAutoRefresh: boolean = false, isFirstLoad: boolean = false): Promise<ActiveChallenge> {
		try {
			const url = `${this.BASE_URL_CHALLENGES}/active?moduleKey=${moduleKey}&isAutoRefresh=${isAutoRefresh}'&isFirstLoad=${isFirstLoad}&isCXone=true`;
			const response = await this.cvApiService.get<ActiveChallenge>(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data;
			} else {
				throw response;
			}
		} catch (error) {
			console.error("Failed getting data for challenges module");
			throw error;
		}
	}

	public async loadCreatableChallenges(): Promise<ChallengeTemplate> {
		try {
			const url = `${this.BASE_URL_TEMPLATES}`;
			const response = await this.cvApiService.get<ChallengeTemplate>(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data;
			} else {
				throw response;
			}
		} catch (error) {
			console.error("Failed getting data for challenge template");
			throw error;
		}
	}

	public async loadInvitableUsers(ruleID: number): Promise<SimpleLogin[]> {
		try {
			const url = `${this.BASE_URL_TEMPLATES}/${ruleID}/invitable`;
			const response = await this.cvApiService.get<SimpleLogin[]>(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data;
			} else {
				throw response;
			}
		} catch (error) {
			console.error("Failed getting data for invitable users");
			throw error;
		}
	}

	public async loadPendingInvites(): Promise<PendingChallenge> {
		try {
			const url = `${this.BASE_URL_CHALLENGES}/pending`;
			const response = await this.cvApiService.get<PendingChallenge>(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data;
			} else {
				throw response;
			}
		} catch (error) {
			console.error("Failed getting data for pending challenges");
			throw error;
		}
	}

	public async loadChallengeStats(): Promise<ChallengeStat> {
		try {
			const url = `/api/v1/gamification/challenge-stats`;
			const response = await this.cvApiService.get<ChallengeStat>(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data;
			} else {
				throw response;
			}
		} catch (error) {
			console.error("Failed getting data for challenge stats");
			throw error;
		}
	}

	public async loadChallengeHistory(): Promise<ChallengeHistory[]> {
		try {
			const url = `${this.BASE_URL_CHALLENGES}/history?includeStartDate=true&includeCreatorName=true`;
			const response = await this.cvApiService.get<ChallengeHistory[]>(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data;
			} else {
				throw response;
			}
		} catch (error) {
			console.error("Failed getting data for challenge history");
			throw error;
		}
	}

	public async acceptInvite(invitation: Invitation): Promise<CurrentChallenge> {
		try {
			let url = `${this.BASE_URL_CHALLENGES}/${invitation.challengeID}/invitation/${invitation.invitedBy}/accept`;
			let response = await this.cvApiService.post(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data as CurrentChallenge;
			} else {
				throw response;
			}
		} catch (err) {
			console.error("Failed accept challenge invitation");
			throw err;
		}
	}

	public async declineInvite(invitation: Invitation): Promise<boolean> {
		try {
			let url = `${this.BASE_URL_CHALLENGES}/${invitation.challengeID}/invitation/${invitation.invitedBy}`;
			let response = await this.cvApiService.delete(url);
			if (response.status_code >= 200 && response.status_code < 300) {
				return true;
			} else {
				throw response;
			}
		} catch (err) {
			console.error("Failed decline challenge invitation");
			throw err;
		}
	}

	public async sendInvites(invite: SendInvite): Promise<boolean> {
		try {
			let url = `${this.BASE_URL_CHALLENGES}/${invite.challengeID}/invitations`;
			let response = await this.cvApiService.post(url, invite.loginIDs);
			if (response.status_code >= 200 && response.status_code < 300) {
				return true;
			} else {
				throw response;
			}
		} catch (err) {
			console.error("Failed send challenge invitations");
			throw err;
		}
	}

	public async createChallenge(challenge: Challenge): Promise<CurrentChallenge> {
		try {
			let url = `${this.BASE_URL_CHALLENGES}`;
			let response = await this.cvApiService.post(url, challenge);
			if (response.status_code >= 200 && response.status_code < 300) {
				return response.data as CurrentChallenge;
			} else {
				throw response;
			}
		} catch (err) {
			console.error("Failed to create a new challenge");
			throw err;
		}
	}

    public async getCurrentUser() {
        let url = `${this.BASE_URL_CHALLENGES}/user`;
        let response = await this.cvApiService.get(url);
        if (response.status_code >= 200 && response.status_code < 300) {
            return response.data;
        } else {
            throw response;
        }
    }

	async loadChallenges(isRefresh = false, moduleKey: string, moduleManager: ModuleManager, callback?: () => void) {
        const challengePromises = [
            this.loadActiveChallenges(moduleKey, isRefresh, true),
            this.loadPendingInvites(),
            this.loadChallengeHistory(),
            this.loadChallengeStats(),
            this.loadCreatableChallenges()
        ];

        await Promise.all(challengePromises).then((results) => {
            const [activeChallenges, pendingChallenges, historyChallenges, stats, metrics] = results;

            this.challenges = {
                activeChallenges: activeChallenges as ActiveChallenge,
                pendingChallenges: pendingChallenges as PendingChallenge,
                historyChallenges: historyChallenges as ChallengeHistory[]
            };
            this.availableCoins = (stats as ChallengeStat).AvailableCoins;
            this.availableMetrics = Object.values(metrics as ChallengeTemplate).map((metric:CreatableChallenge) => metric);
            
            moduleManager.showLoader = false;
			moduleManager.setAutoRefreshReady();
        }).catch((error) => {
			moduleManager.handleLoadError(error, isRefresh);
            moduleManager.showLoader = false;
        }).finally(() => {
			callback && callback();
		});
    }

	addChallenge(challenge: CurrentChallenge) {
		challenge.Details.ParticipantCount = challenge.Details.ParticipantCount === 0 ? 1 : challenge.Details.ParticipantCount;
		challenge.TotalParticipants = challenge.TotalParticipants === 0 ? 1 : challenge.TotalParticipants;
		if (challenge.IsRunning) {
			// add at the beginning of the list
			this.challenges.activeChallenges.RunningChallenges.unshift(challenge);
		} else {
			this.challenges.activeChallenges.UpcomingChallenges.unshift(challenge);
		}
	}

	removeChallenge(challengeID: number, challengeStatus: ChallengeStatus) {
		if (challengeStatus === 'ongoing') {
			this.challenges.activeChallenges.RunningChallenges = this.challenges.activeChallenges.RunningChallenges.filter(c => c.ChallengeID !== challengeID);
		} else if(challengeStatus === 'upcoming') {
			this.challenges.activeChallenges.UpcomingChallenges = this.challenges.activeChallenges.UpcomingChallenges.filter(c => c.ChallengeID !== challengeID);
		} else if(challengeStatus === 'pending') {
			this.challenges.pendingChallenges.Invitations = this.challenges.pendingChallenges.Invitations.filter(c => c.ChallengeID !== challengeID);
		}
	}

	updateUserCoins(coins: number) {
		this.availableCoins += coins;
	}

}
